/* eslint-disable max-lines */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import Button from '@components/atoms/ui-kit/button';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal';
import {
  RotateCcw,
  RefreshCcw,
  Trash2,
  XCircle,
  Gauge,
  Eye,
  AlertTriangle,
  MousePointer,
  Zap,
  Clock,
  Globe,
  FileText,
  Info
} from 'lucide-react';

/* eslint-disable no-restricted-syntax -- Semantic operation status colors (red=destructive, etc.) used for icon tinting */
import {
  OperationsSession,
  BulkAction,
  STATUS_COLORS
} from '@utils/types/back-office/operations';
import {
  useRequeueSession,
  useRestartSession,
  useKillSession,
  useCleanupSession,
  useChangePriority,
  useBulkSessionAction
} from '@utils/api/back-office/operations';
import toast from 'react-hot-toast';

interface ContextualActionsProps {
  selectedSessions: OperationsSession[];
  onViewDetails: (session: OperationsSession) => void;
  onClearSelection: () => void;
}

type ConfirmAction = {
  type: 'single' | 'bulk';
  action: string;
  sessionId?: string;
  bulkAction?: BulkAction;
  title: string;
  message: string;
  destructive: boolean;
};

export default function ContextualActions({
  selectedSessions,
  onViewDetails,
  onClearSelection
}: ContextualActionsProps) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(
    null
  );
  const [priorityValue, setPriorityValue] = useState<number>(5);

  const { mutate: requeueSession, isLoading: requeueLoading } =
    useRequeueSession();
  const { mutate: restartSession, isLoading: restartLoading } =
    useRestartSession();
  const { mutate: killSession, isLoading: killLoading } = useKillSession();
  const { mutate: cleanupSession, isLoading: cleanupLoading } =
    useCleanupSession();
  const { mutate: changePriority, isLoading: priorityLoading } =
    useChangePriority();
  const { mutate: bulkAction, isLoading: bulkLoading } = useBulkSessionAction();

  const isLoading =
    requeueLoading ||
    restartLoading ||
    killLoading ||
    cleanupLoading ||
    priorityLoading ||
    bulkLoading;

  const singleSession =
    selectedSessions.length === 1 ? selectedSessions[0] : null;

  // Define available actions per status
  const getActionsForStatus = (status: string) => {
    switch (status) {
      case 'uploading':
      case 'uploaded':
        return ['cancel'];
      case 'queued':
        return ['view-details', 'change-priority', 'kill'];
      case 'processing':
        return ['view-details', 'change-priority', 'kill'];
      case 'streaming':
        return ['view-details'];
      case 'finished':
        return ['view-details', 'cleanup'];
      case 'error':
        return ['requeue', 'view-error', 'kill'];
      case 'stranded':
        return ['restart', 'requeue', 'kill', 'cleanup'];
      default:
        return ['view-details'];
    }
  };

  const handleSingleAction = (action: string) => {
    if (!singleSession) return;

    const confirmActions: Record<string, ConfirmAction> = {
      kill: {
        type: 'single',
        action: 'kill',
        sessionId: singleSession.session_id,
        title: t('kill-session'),
        message: t('are-you-sure-kill-session'),
        destructive: true
      },
      cleanup: {
        type: 'single',
        action: 'cleanup',
        sessionId: singleSession.session_id,
        title: t('cleanup-session'),
        message: t('are-you-sure-cleanup-session'),
        destructive: true
      },
      restart: {
        type: 'single',
        action: 'restart',
        sessionId: singleSession.session_id,
        title: t('restart-session'),
        message: t('are-you-sure-restart-session'),
        destructive: false
      },
      requeue: {
        type: 'single',
        action: 'requeue',
        sessionId: singleSession.session_id,
        title: t('requeue-session'),
        message: t('are-you-sure-requeue-session'),
        destructive: false
      }
    };

    if (confirmActions[action]) {
      setConfirmAction(confirmActions[action]);
    } else if (action === 'view-details' || action === 'view-error') {
      onViewDetails(singleSession);
    }
  };

  const handleBulkAction = (action: BulkAction) => {
    const sessionIds = selectedSessions.map(s => s.session_id);
    setConfirmAction({
      type: 'bulk',
      action,
      bulkAction: action,
      title: t(`bulk-${action}`),
      message: t(`are-you-sure-bulk-${action}`, { count: sessionIds.length }),
      destructive: action === 'kill' || action === 'cleanup'
    });
  };

  const executeAction = () => {
    if (!confirmAction) return;

    const onSuccess = () => {
      toast.success(t('action-completed-successfully'));
      setConfirmAction(null);
      onClearSelection();
    };

    const onError = () => {
      toast.error(t('action-failed'));
    };

    if (confirmAction.type === 'single' && confirmAction.sessionId) {
      switch (confirmAction.action) {
        case 'kill':
          killSession(confirmAction.sessionId, { onSuccess, onError });
          break;
        case 'cleanup':
          cleanupSession(confirmAction.sessionId, { onSuccess, onError });
          break;
        case 'restart':
          restartSession(confirmAction.sessionId, { onSuccess, onError });
          break;
        case 'requeue':
          requeueSession(confirmAction.sessionId, { onSuccess, onError });
          break;
      }
    } else if (confirmAction.type === 'bulk' && confirmAction.bulkAction) {
      bulkAction(
        {
          session_ids: selectedSessions.map(s => s.session_id),
          action: confirmAction.bulkAction
        },
        { onSuccess, onError }
      );
    }
  };

  const handlePriorityChange = () => {
    if (!singleSession) return;
    changePriority(
      { sessionId: singleSession.session_id, priority: priorityValue },
      {
        onSuccess: () => {
          toast.success(t('priority-updated'));
        },
        onError: () => {
          toast.error(t('failed-to-update-priority'));
        }
      }
    );
  };

  const formatDuration = (seconds: number | null) => {
    if (seconds === null || seconds === undefined) return '-';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m`;
    return `${seconds}s`;
  };

  // No selection state
  if (selectedSessions.length === 0) {
    return (
      <div className="h-full flex flex-col p-4">
        {/* Header */}
        <div
          className="flex items-center gap-2 pb-3 mb-4 border-b"
          style={{ borderColor: getColour('border') }}
        >
          <Zap className="h-4 w-4" style={{ color: getColour('activeText') }} />
          <h3
            className="text-sm font-semibold"
            style={{ color: getColour('normalText') }}
          >
            {t('quick-actions')}
          </h3>
        </div>

        {/* Empty state */}
        <div className="flex-1 flex flex-col items-center justify-center text-center">
          <div
            className="p-4 rounded-full mb-4"
            style={{ backgroundColor: getColour('secondLayer') }}
          >
            <MousePointer
              className="h-8 w-8"
              style={{ color: getColour('inactiveText') }}
            />
          </div>
          <h4
            className="text-sm font-medium mb-2"
            style={{ color: getColour('normalText') }}
          >
            {t('no-session-selected')}
          </h4>
          <p
            className="text-xs leading-relaxed max-w-[200px]"
            style={{ color: getColour('inactiveText') }}
          >
            {t('select-session-to-see-actions')}
          </p>
        </div>

        {/* Help section */}
        <div
          className="mt-4 p-3 rounded-2xl"
          style={{ backgroundColor: getColour('secondLayer') }}
        >
          <div className="flex items-start gap-2">
            <Info
              className="h-4 w-4 flex-shrink-0 mt-0.5"
              style={{ color: getColour('activeText') }}
            />
            <div>
              <p
                className="text-xs font-medium mb-1"
                style={{ color: getColour('normalText') }}
              >
                {t('tip')}
              </p>
              <p
                className="text-xs leading-relaxed"
                style={{ color: getColour('inactiveText') }}
              >
                {t('click-row-to-select-session')}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Multiple selection state
  if (selectedSessions.length > 1) {
    return (
      <div className="h-full flex flex-col p-4">
        {/* Header */}
        <div
          className="flex items-center gap-2 pb-3 mb-4 border-b"
          style={{ borderColor: getColour('border') }}
        >
          <Zap className="h-4 w-4" style={{ color: getColour('activeText') }} />
          <h3
            className="text-sm font-semibold"
            style={{ color: getColour('normalText') }}
          >
            {t('bulk-actions')}
          </h3>
        </div>

        {/* Selection info */}
        <div
          className="rounded-2xl p-3 mb-4"
          style={{
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.2)'
          }}
        >
          <p className="text-sm font-semibold" style={{ color: '#3B82F6' }}>
            {selectedSessions.length} {t('sessions-selected')}
          </p>
          <p
            className="text-xs mt-1"
            style={{ color: getColour('inactiveText') }}
          >
            {t('bulk-actions-will-apply-to-all')}
          </p>
        </div>

        {/* Actions */}
        <div className="space-y-3 flex-1">
          <div
            className="rounded-2xl p-3"
            style={{ backgroundColor: getColour('secondLayer') }}
          >
            <div className="flex items-center gap-2 mb-2">
              <RefreshCcw
                className="h-4 w-4"
                style={{ color: getColour('activeText') }}
              />
              <span
                className="text-xs font-medium"
                style={{ color: getColour('normalText') }}
              >
                {t('requeue-all')}
              </span>
            </div>
            <p
              className="text-xs mb-3"
              style={{ color: getColour('inactiveText') }}
            >
              {t('requeue-all-description')}
            </p>
            <Button
              label={t('requeue-all')}
              variant="secondary"
              size="small"
              icon={<RefreshCcw className="h-4 w-4" />}
              onClick={() => handleBulkAction('requeue')}
              disabled={isLoading}
            />
          </div>

          <div
            className="rounded-2xl p-3"
            style={{ backgroundColor: getColour('secondLayer') }}
          >
            <div className="flex items-center gap-2 mb-2">
              <XCircle className="h-4 w-4" style={{ color: '#EF4444' }} />
              <span
                className="text-xs font-medium"
                style={{ color: getColour('normalText') }}
              >
                {t('kill-all')}
              </span>
            </div>
            <p
              className="text-xs mb-3"
              style={{ color: getColour('inactiveText') }}
            >
              {t('kill-all-description')}
            </p>
            <Button
              label={t('kill-all')}
              variant="secondary"
              size="small"
              icon={<XCircle className="h-4 w-4" />}
              onClick={() => handleBulkAction('kill')}
              disabled={isLoading}
              className="!text-red-500 hover:!bg-red-50"
            />
          </div>

          <div
            className="rounded-2xl p-3"
            style={{ backgroundColor: getColour('secondLayer') }}
          >
            <div className="flex items-center gap-2 mb-2">
              <Trash2 className="h-4 w-4" style={{ color: '#EF4444' }} />
              <span
                className="text-xs font-medium"
                style={{ color: getColour('normalText') }}
              >
                {t('cleanup-all')}
              </span>
            </div>
            <p
              className="text-xs mb-3"
              style={{ color: getColour('inactiveText') }}
            >
              {t('cleanup-all-description')}
            </p>
            <Button
              label={t('cleanup-all')}
              variant="secondary"
              size="small"
              icon={<Trash2 className="h-4 w-4" />}
              onClick={() => handleBulkAction('cleanup')}
              disabled={isLoading}
              className="!text-red-500 hover:!bg-red-50"
            />
          </div>
        </div>

        <div
          className="mt-4 pt-3 border-t flex-shrink-0"
          style={{ borderColor: getColour('border') }}
        >
          <Button
            label={t('clear-selection')}
            variant="text"
            size="small"
            onClick={onClearSelection}
            className="w-full"
          />
        </div>

        <ConfirmationModal
          isOpen={!!confirmAction}
          onClose={() => setConfirmAction(null)}
          onConfirm={executeAction}
          title={confirmAction?.title || ''}
          caption={confirmAction?.message || ''}
          confirmLabel={t('confirm')}
          cancelLabel={t('cancel')}
          destructive={confirmAction?.destructive}
          confirmDisabled={isLoading}
        />
      </div>
    );
  }

  // Single selection state
  const availableActions = singleSession
    ? getActionsForStatus(singleSession.status)
    : [];
  const statusColors =
    STATUS_COLORS[singleSession?.status || ''] || STATUS_COLORS['processing'];

  return (
    <div className="h-full flex flex-col p-1">
      {/* Header */}
      <div
        className="flex items-center gap-2 pb-3 mb-4 border-b"
        style={{ borderColor: getColour('border') }}
      >
        <Zap className="h-4 w-4" style={{ color: getColour('activeText') }} />
        <h3
          className="text-sm font-semibold"
          style={{ color: getColour('normalText') }}
        >
          {t('session-actions')}
        </h3>
      </div>

      {/* Session info card */}
      <div
        className="rounded-2xl p-3 mb-4"
        style={{ backgroundColor: getColour('secondLayer') }}
      >
        {/* Status badge */}
        <div className="flex items-center justify-between mb-3">
          <span
            className="text-xs"
            style={{ color: getColour('inactiveText') }}
          >
            {t('status')}
          </span>
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
            style={{
              backgroundColor: statusColors.bg,
              color: statusColors.text
            }}
          >
            {t(singleSession?.status || '')}
          </span>
        </div>

        {/* Session details */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <FileText
              className="h-3.5 w-3.5 flex-shrink-0"
              style={{ color: getColour('inactiveText') }}
            />
            <p
              className="text-xs font-mono truncate flex-1"
              style={{ color: getColour('normalText') }}
              title={singleSession?.session_id}
            >
              {singleSession?.session_id}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Globe
              className="h-3.5 w-3.5 flex-shrink-0"
              style={{ color: getColour('inactiveText') }}
            />
            <p
              className="text-xs truncate flex-1"
              style={{ color: getColour('normalText') }}
            >
              {singleSession?.organization_name}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Clock
              className="h-3.5 w-3.5 flex-shrink-0"
              style={{ color: getColour('inactiveText') }}
            />
            <p className="text-xs" style={{ color: getColour('normalText') }}>
              {formatDuration(singleSession?.duration ?? null)}
            </p>
          </div>

          {singleSession?.filename && (
            <div className="flex items-center gap-2">
              <FileText
                className="h-3.5 w-3.5 flex-shrink-0"
                style={{ color: getColour('inactiveText') }}
              />
              <p
                className="text-xs truncate flex-1"
                style={{ color: getColour('normalText') }}
                title={singleSession.filename}
              >
                {singleSession.filename}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Available actions */}
      <div className="flex-1 space-y-3 overflow-y-auto">
        <h4
          className="text-xs font-medium uppercase tracking-wide"
          style={{ color: getColour('inactiveText') }}
        >
          {t('available-actions')}
        </h4>

        {availableActions.includes('view-details') && (
          <div
            className="rounded-2xl p-3"
            style={{ backgroundColor: getColour('secondLayer') }}
          >
            <div className="flex items-center gap-2 mb-1">
              <Eye
                className="h-4 w-4"
                style={{ color: getColour('activeText') }}
              />
              <span
                className="text-xs font-medium"
                style={{ color: getColour('normalText') }}
              >
                {t('view-details')}
              </span>
            </div>
            <p
              className="text-xs mb-2"
              style={{ color: getColour('inactiveText') }}
            >
              {t('view-session-details-description')}
            </p>
            <Button
              label={t('view-details')}
              variant="secondary"
              size="small"
              icon={<Eye className="h-4 w-4" />}
              onClick={() => onViewDetails(singleSession!)}
            />
          </div>
        )}

        {availableActions.includes('view-error') && (
          <div
            className="rounded-2xl p-3"
            style={{
              backgroundColor: 'rgba(239, 68, 68, 0.05)',
              border: '1px solid rgba(239, 68, 68, 0.1)'
            }}
          >
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="h-4 w-4" style={{ color: '#EF4444' }} />
              <span
                className="text-xs font-medium"
                style={{ color: getColour('normalText') }}
              >
                {t('view-error-log')}
              </span>
            </div>
            <p
              className="text-xs mb-2"
              style={{ color: getColour('inactiveText') }}
            >
              {t('view-error-log-description')}
            </p>
            <Button
              label={t('view-error-log')}
              variant="secondary"
              size="small"
              icon={<AlertTriangle className="h-4 w-4" />}
              onClick={() => onViewDetails(singleSession!)}
            />
          </div>
        )}

        {availableActions.includes('change-priority') && (
          <div
            className="rounded-2xl p-3"
            style={{ backgroundColor: getColour('secondLayer') }}
          >
            <div className="flex items-center gap-2 mb-1">
              <Gauge
                className="h-4 w-4"
                style={{ color: getColour('activeText') }}
              />
              <span
                className="text-xs font-medium"
                style={{ color: getColour('normalText') }}
              >
                {t('change-priority')}
              </span>
            </div>
            <p
              className="text-xs mb-2"
              style={{ color: getColour('inactiveText') }}
            >
              {t('change-priority-description')}
            </p>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={1000}
                value={priorityValue}
                onChange={e => setPriorityValue(Number(e.target.value))}
                className="flex-1 px-2 py-1 text-sm rounded-2xl border"
                style={{
                  backgroundColor: getColour('baseLayer'),
                  borderColor: getColour('border'),
                  color: getColour('normalText')
                }}
              />
              <Button
                label={t('apply')}
                size="small"
                onClick={handlePriorityChange}
                disabled={priorityLoading}
              />
            </div>
          </div>
        )}

        {availableActions.includes('requeue') && (
          <div
            className="rounded-2xl p-3"
            style={{ backgroundColor: getColour('secondLayer') }}
          >
            <div className="flex items-center gap-2 mb-1">
              <RefreshCcw
                className="h-4 w-4"
                style={{ color: getColour('activeText') }}
              />
              <span
                className="text-xs font-medium"
                style={{ color: getColour('normalText') }}
              >
                {t('requeue')}
              </span>
            </div>
            <p
              className="text-xs mb-2"
              style={{ color: getColour('inactiveText') }}
            >
              {t('requeue-description')}
            </p>
            <Button
              label={t('requeue')}
              variant="secondary"
              size="small"
              icon={<RefreshCcw className="h-4 w-4" />}
              onClick={() => handleSingleAction('requeue')}
              disabled={isLoading}
            />
          </div>
        )}

        {availableActions.includes('restart') && (
          <div
            className="rounded-2xl p-3"
            style={{ backgroundColor: getColour('secondLayer') }}
          >
            <div className="flex items-center gap-2 mb-1">
              <RotateCcw
                className="h-4 w-4"
                style={{ color: getColour('activeText') }}
              />
              <span
                className="text-xs font-medium"
                style={{ color: getColour('normalText') }}
              >
                {t('restart')}
              </span>
            </div>
            <p
              className="text-xs mb-2"
              style={{ color: getColour('inactiveText') }}
            >
              {t('restart-description')}
            </p>
            <Button
              label={t('restart')}
              variant="secondary"
              size="small"
              icon={<RotateCcw className="h-4 w-4" />}
              onClick={() => handleSingleAction('restart')}
              disabled={isLoading}
            />
          </div>
        )}

        {availableActions.includes('kill') && (
          <div
            className="rounded-2xl p-3"
            style={{
              backgroundColor: 'rgba(239, 68, 68, 0.05)',
              border: '1px solid rgba(239, 68, 68, 0.1)'
            }}
          >
            <div className="flex items-center gap-2 mb-1">
              <XCircle className="h-4 w-4" style={{ color: '#EF4444' }} />
              <span
                className="text-xs font-medium"
                style={{ color: getColour('normalText') }}
              >
                {t('kill')}
              </span>
            </div>
            <p
              className="text-xs mb-2"
              style={{ color: getColour('inactiveText') }}
            >
              {t('kill-description')}
            </p>
            <Button
              label={t('kill')}
              variant="secondary"
              size="small"
              icon={<XCircle className="h-4 w-4" />}
              onClick={() => handleSingleAction('kill')}
              disabled={isLoading}
              className="!text-red-500 hover:!bg-red-50"
            />
          </div>
        )}

        {availableActions.includes('cancel') && (
          <div
            className="rounded-2xl p-3"
            style={{
              backgroundColor: 'rgba(239, 68, 68, 0.05)',
              border: '1px solid rgba(239, 68, 68, 0.1)'
            }}
          >
            <div className="flex items-center gap-2 mb-1">
              <XCircle className="h-4 w-4" style={{ color: '#EF4444' }} />
              <span
                className="text-xs font-medium"
                style={{ color: getColour('normalText') }}
              >
                {t('cancel-upload')}
              </span>
            </div>
            <p
              className="text-xs mb-2"
              style={{ color: getColour('inactiveText') }}
            >
              {t('cancel-upload-description')}
            </p>
            <Button
              label={t('cancel-upload')}
              variant="secondary"
              size="small"
              icon={<XCircle className="h-4 w-4" />}
              onClick={() => handleSingleAction('kill')}
              disabled={isLoading}
              className="!text-red-500 hover:!bg-red-50"
            />
          </div>
        )}

        {availableActions.includes('cleanup') && (
          <div
            className="rounded-2xl p-3"
            style={{
              backgroundColor: 'rgba(239, 68, 68, 0.05)',
              border: '1px solid rgba(239, 68, 68, 0.1)'
            }}
          >
            <div className="flex items-center gap-2 mb-1">
              <Trash2 className="h-4 w-4" style={{ color: '#EF4444' }} />
              <span
                className="text-xs font-medium"
                style={{ color: getColour('normalText') }}
              >
                {t('cleanup')}
              </span>
            </div>
            <p
              className="text-xs mb-2"
              style={{ color: getColour('inactiveText') }}
            >
              {t('cleanup-description')}
            </p>
            <Button
              label={t('cleanup')}
              variant="secondary"
              size="small"
              icon={<Trash2 className="h-4 w-4" />}
              onClick={() => handleSingleAction('cleanup')}
              disabled={isLoading}
              className="!text-red-500 hover:!bg-red-50"
            />
          </div>
        )}
      </div>

      <div
        className="mt-4 pt-3 border-t flex-shrink-0"
        style={{ borderColor: getColour('border') }}
      >
        <Button
          label={t('clear-selection')}
          variant="text"
          size="small"
          onClick={onClearSelection}
          className="w-full"
        />
      </div>

      <ConfirmationModal
        isOpen={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={executeAction}
        title={confirmAction?.title || ''}
        caption={confirmAction?.message || ''}
        confirmLabel={t('confirm')}
        cancelLabel={t('cancel')}
        destructive={confirmAction?.destructive}
        confirmDisabled={isLoading}
      />
    </div>
  );
}
